import type { Timestamp } from '@bufbuild/protobuf/wkt'

// Event date presets (future dates)
export type EventDatePreset = 'this_week' | 'this_month' | 'next_3_months'

// Received at presets (past dates)
export type ReceivedAtPreset = 'past_3_days' | 'past_week' | 'past_month'

export interface DateRange {
  start: Date
  end: Date
}

export const EVENT_DATE_PRESET_LABELS: Record<EventDatePreset, string> = {
  this_week: 'This Week',
  this_month: 'This Month',
  next_3_months: 'Next 3 Months',
}

export const RECEIVED_AT_PRESET_LABELS: Record<ReceivedAtPreset, string> = {
  past_3_days: 'Past 3 Days',
  past_week: 'Past Week',
  past_month: 'Past Month',
}

// Union type for all date presets
export type DatePreset = EventDatePreset | ReceivedAtPreset

// Date filter mode (before or after the date)
export type DateFilterMode = 'before' | 'after'

// Combined date filter value
export interface DateFilterValue {
  preset: DatePreset
  mode: DateFilterMode
}

/**
 * Get the start and end dates for a given preset
 */
export function getDateRangeForPreset(preset: DatePreset): DateRange {
  const now = new Date()
  const start = new Date(now)
  const end = new Date(now)

  switch (preset) {
    case 'this_week': {
      // Start of current week (Sunday)
      const dayOfWeek = start.getDay()
      start.setDate(start.getDate() - dayOfWeek)
      start.setHours(0, 0, 0, 0)

      // End of current week (Saturday)
      end.setDate(start.getDate() + 6)
      end.setHours(23, 59, 59, 999)
      break
    }

    case 'this_month': {
      // Start of current month
      start.setDate(1)
      start.setHours(0, 0, 0, 0)

      // End of current month
      end.setMonth(end.getMonth() + 1, 0)
      end.setHours(23, 59, 59, 999)
      break
    }

    case 'next_3_months': {
      // Today
      start.setHours(0, 0, 0, 0)

      // 90 days from today
      end.setDate(end.getDate() + 90)
      end.setHours(23, 59, 59, 999)
      break
    }

    case 'past_3_days': {
      // 3 days ago
      start.setDate(start.getDate() - 3)
      start.setHours(0, 0, 0, 0)

      // Now
      end.setHours(23, 59, 59, 999)
      break
    }

    case 'past_week': {
      // 7 days ago
      start.setDate(start.getDate() - 7)
      start.setHours(0, 0, 0, 0)

      // Now
      end.setHours(23, 59, 59, 999)
      break
    }

    case 'past_month': {
      // 30 days ago
      start.setDate(start.getDate() - 30)
      start.setHours(0, 0, 0, 0)

      // Now
      end.setHours(23, 59, 59, 999)
      break
    }
  }

  return { start, end }
}

/**
 * Convert a protobuf Timestamp to a JavaScript Date
 */
export function timestampToDate(timestamp: Timestamp): Date {
  const seconds = typeof timestamp.seconds === 'bigint'
    ? Number(timestamp.seconds)
    : timestamp.seconds

  return new Date(seconds * 1000)
}

/**
 * Check if a protobuf Timestamp falls within a date range
 */
export function isDateInRange(
  timestamp: Timestamp | undefined,
  range: DateRange
): boolean {
  if (!timestamp) return false

  const date = timestampToDate(timestamp)
  return date >= range.start && date <= range.end
}

/**
 * Check if a protobuf Timestamp matches a date preset
 */
export function matchesDatePreset(
  timestamp: Timestamp | undefined,
  preset: DatePreset
): boolean {
  if (!timestamp) return false

  const range = getDateRangeForPreset(preset)
  return isDateInRange(timestamp, range)
}

/**
 * Check if a timestamp is before a date range
 */
export function isDateBefore(
  timestamp: Timestamp | undefined,
  range: DateRange
): boolean {
  if (!timestamp) return false

  const date = timestampToDate(timestamp)
  return date < range.start
}

/**
 * Check if a timestamp is after a date range
 */
export function isDateAfter(
  timestamp: Timestamp | undefined,
  range: DateRange
): boolean {
  if (!timestamp) return false

  const date = timestampToDate(timestamp)
  return date > range.end
}

/**
 * Check if a protobuf Timestamp matches a date filter value (preset + mode)
 */
export function matchesDateFilter(
  timestamp: Timestamp | undefined,
  filterValue: DateFilterValue | DatePreset | string
): boolean {
  if (!timestamp) return false

  // Handle legacy string preset format (backward compatibility)
  if (typeof filterValue === 'string') {
    return matchesDatePreset(timestamp, filterValue as DatePreset)
  }

  // Handle new DateFilterValue format
  const { preset, mode } = filterValue as DateFilterValue
  const range = getDateRangeForPreset(preset)

  if (mode === 'before') {
    return isDateBefore(timestamp, range)
  } else if (mode === 'after') {
    return isDateAfter(timestamp, range)
  }

  // Default to within range
  return isDateInRange(timestamp, range)
}

/**
 * Custom date range for user-selected from/to dates
 */
export interface CustomDateRange {
  from?: Date
  to?: Date
}

/**
 * Check if a protobuf Timestamp falls within a custom date range
 */
export function isDateInCustomRange(
  timestamp: Timestamp | undefined,
  range: CustomDateRange
): boolean {
  if (!timestamp) return false
  if (!range.from && !range.to) return true // No filter applied

  const date = timestampToDate(timestamp)

  if (range.from && range.to) {
    // Both from and to specified - check within range
    const start = new Date(range.from)
    start.setHours(0, 0, 0, 0)
    const end = new Date(range.to)
    end.setHours(23, 59, 59, 999)
    return date >= start && date <= end
  }

  if (range.from) {
    // Only from specified - check date is on or after
    const start = new Date(range.from)
    start.setHours(0, 0, 0, 0)
    return date >= start
  }

  if (range.to) {
    // Only to specified - check date is on or before
    const end = new Date(range.to)
    end.setHours(23, 59, 59, 999)
    return date <= end
  }

  return true
}
